import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { ApiError, api, type AdminUser } from "./api.js";

// localStorage, not sessionStorage: sessionStorage is scoped to a single tab,
// so opening the dashboard in a second tab (or reopening the browser) landed on
// the login screen even though the JWT was still valid for hours. The session
// is now shared by every tab of the origin and survives a restart; its lifetime
// is bounded by the JWT's own `exp` claim — JWT_TTL_SECONDS on the cloud API,
// 24h by default — and an expired token is dropped on read instead of being
// handed to the API. The trade-off is deliberate: the token outlives the tab,
// so `logout` (and any 401) must clear it everywhere, which the `storage`
// listener below takes care of.
const TOKEN_KEY = "sage-mcp-admin-token";
const USER_KEY = "sage-mcp-admin-user";
const ACTIVITY_KEY = "sage-mcp-admin-last-activity";
// Why the last session ended, so every tab lands on the login screen with the
// same explanation instead of one of them silently reappearing. Cleared on the
// next successful sign-in.
const END_REASON_KEY = "sage-mcp-admin-session-end";

// How long before the JWT expires do we try to refresh it? 10 min gives plenty
// of slack if the network is flaky.
const REFRESH_LEAD_SECONDS = 10 * 60;

// Idle purge — this is what buys back the protection localStorage gave up. The
// JWT can be valid for another 20 hours; if nobody has touched the dashboard
// for IDLE_TIMEOUT_MINUTES we drop it anyway and fall back to the login screen,
// so an unattended browser is not a standing session. The stamp lives in
// localStorage, so working in any tab keeps every tab alive.
// Override at build time with VITE_ADMIN_IDLE_TIMEOUT_MINUTES; 0 disables it.
const DEFAULT_IDLE_TIMEOUT_MINUTES = 60;
// Don't rewrite the stamp on every keystroke — once per minute is enough
// resolution for an hour-long window and keeps localStorage writes cheap.
const ACTIVITY_WRITE_THROTTLE_MS = 60_000;

const IDLE_TIMEOUT_MINUTES = readIdleTimeoutMinutes();
const IDLE_TIMEOUT_MS =
  IDLE_TIMEOUT_MINUTES > 0 ? IDLE_TIMEOUT_MINUTES * 60_000 : Number.POSITIVE_INFINITY;
// Poll often enough that the sign-out is prompt without being a busy loop. The
// second term only matters for the short timeouts used in manual testing.
const IDLE_CHECK_INTERVAL_MS = Math.max(5_000, Math.min(30_000, IDLE_TIMEOUT_MS / 4));

// Browser events that count as "somebody is still there". Deliberately not
// `mousemove`/`scroll`: a nudged mouse or a kinetic scroll on an abandoned
// laptop shouldn't hold a session open.
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

function readIdleTimeoutMinutes(): number {
  const raw = import.meta.env.VITE_ADMIN_IDLE_TIMEOUT_MINUTES as string | undefined;
  if (raw === undefined || raw === "") return DEFAULT_IDLE_TIMEOUT_MINUTES;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : DEFAULT_IDLE_TIMEOUT_MINUTES;
}

// Private-mode Safari and "block all cookies" make localStorage throw on every
// access. Degrade to an in-memory session rather than crashing the whole app.
function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — session stays in React state only */
  }
}

function storageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// Sessions used to live in sessionStorage. Move an in-flight one over on first
// load so the switch doesn't sign everybody out.
function migrateLegacySession(): void {
  try {
    const legacyToken = window.sessionStorage.getItem(TOKEN_KEY);
    if (legacyToken && !storageGet(TOKEN_KEY)) {
      storageSet(TOKEN_KEY, legacyToken);
      const legacyUser = window.sessionStorage.getItem(USER_KEY);
      if (legacyUser) storageSet(USER_KEY, legacyUser);
    }
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

interface AuthContextValue {
  token: string | null;
  user: AdminUser | null;
  emergency: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface StoredUser {
  user: AdminUser;
  emergency: boolean;
}

function readStoredUser(): StoredUser | null {
  const raw = storageGet(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

function readLastActivity(): number | null {
  const raw = storageGet(ACTIVITY_KEY);
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function markActivity(now = Date.now()): void {
  storageSet(ACTIVITY_KEY, String(now));
}

// Why a stored session stopped being usable. An explicit sign-out and a 401
// pass no reason — there is nothing to explain in the first case, and the
// second already means "your credentials are gone".
type SessionEnd = "expired" | "idle";

function dropStoredSession(reason?: SessionEnd): void {
  // Reason first, token second. Other tabs wake on the token's `storage` event
  // and read the reason right away — writing it afterwards makes them race and
  // land on a bare login form with no explanation.
  if (reason) storageSet(END_REASON_KEY, reason);
  else storageRemove(END_REASON_KEY);
  storageRemove(TOKEN_KEY);
  storageRemove(USER_KEY);
  storageRemove(ACTIVITY_KEY);
}

function readSessionEnd(): SessionEnd | null {
  const raw = storageGet(END_REASON_KEY);
  return raw === "idle" || raw === "expired" ? raw : null;
}

interface StoredSession {
  token: string | null;
  ended: SessionEnd | null;
}

// Read the persisted session, discarding a token that has expired or gone idle.
// Doing it here rather than on the first 401 means the app never renders its
// shell only to bounce back to the login screen a request later.
//
// A session with no activity stamp at all — one written by a build that predates
// the idle purge — counts as live: signing somebody out over a timestamp that
// never existed would be wrong. The stamp is written on the way in.
function readStoredSession(now = Date.now()): StoredSession {
  const token = storageGet(TOKEN_KEY);
  // No token: another tab may have ended the session — carry its reason over so
  // a freshly opened tab explains itself too.
  if (!token) return { token: null, ended: readSessionEnd() };
  const expMs = jwtExpiryMs(token);
  if (expMs !== null && expMs <= now) {
    dropStoredSession("expired");
    return { token: null, ended: "expired" };
  }
  const last = readLastActivity();
  if (last !== null && now - last > IDLE_TIMEOUT_MS) {
    dropStoredSession("idle");
    return { token: null, ended: "idle" };
  }
  return { token, ended: null };
}

function jwtExpiryMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const pad = parts[1].length % 4;
    const b64 = (pad ? parts[1] + "=".repeat(4 - pad) : parts[1])
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const payload = JSON.parse(atob(b64)) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const idleSignOutMessage = useCallback(
    () =>
      t`Signed out after ${IDLE_TIMEOUT_MINUTES} minutes without activity. Please sign in again.`,
    [t],
  );
  const [bootstrap] = useState<StoredSession>(() => {
    migrateLegacySession();
    return readStoredSession();
  });
  const [token, setToken] = useState<string | null>(bootstrap.token);
  const [stored, setStored] = useState<StoredUser | null>(() =>
    bootstrap.token ? readStoredUser() : null,
  );
  const [loading, setLoading] = useState(false);
  // A session dropped before the first render still owes the operator an
  // explanation — the login screen renders `error`.
  const [error, setError] = useState<string | null>(() =>
    bootstrap.ended === "idle" ? idleSignOutMessage() : null,
  );
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback((reason?: SessionEnd) => {
    dropStoredSession(reason);
    setToken(null);
    setStored(null);
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const persist = useCallback((nextToken: string, next: StoredUser) => {
    storageSet(TOKEN_KEY, nextToken);
    storageSet(USER_KEY, JSON.stringify(next));
    // Signing in is activity; without this the idle watchdog would inherit the
    // stamp of whatever session ran before.
    markActivity();
    storageRemove(END_REASON_KEY);
    setToken(nextToken);
    setStored(next);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      if (!email.trim() || !password) {
        setError(t`Email and password are required`);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { token: jwt, user } = await api.login(email, password);
        persist(jwt, { user, emergency: false });
      } catch (err) {
        if (err instanceof ApiError && err.code === "E_UNAUTHORIZED") {
          setError(t`Invalid email or password.`);
        } else if (err instanceof ApiError && err.code === "E_NETWORK") {
          setError(
            t`Could not reach the cloud API. Check VITE_CLOUD_API_URL or that the service is running.`,
          );
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setLoading(false);
      }
    },
    [persist, t],
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  // On mount (and whenever the token changes) verify the session with the API.
  // A rotated JWT secret or a disabled user shows up as 401 → forced sign-out.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .me(token)
      .then((res) => {
        if (cancelled) return;
        setStored({ user: res.user, emergency: Boolean(res.emergency) });
        storageSet(
          USER_KEY,
          JSON.stringify({ user: res.user, emergency: Boolean(res.emergency) }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "E_UNAUTHORIZED") {
          clearSession();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  // Idle watchdog. Two ways a session ends here: the periodic check catches a
  // tab left open and forgotten, and `touch` catches the operator coming back
  // to a tab that went stale while the timer was throttled or the machine
  // asleep. `touch` tests the elapsed time *before* stamping — stamping first
  // would let a single click resurrect a session that had already timed out.
  useEffect(() => {
    if (!token || IDLE_TIMEOUT_MS === Number.POSITIVE_INFINITY) return;
    if (readLastActivity() === null) markActivity();

    const endForIdle = () => {
      clearSession("idle");
      setError(idleSignOutMessage());
    };

    const touch = () => {
      const now = Date.now();
      const last = readLastActivity();
      if (last === null) {
        markActivity(now);
        return;
      }
      if (now - last > IDLE_TIMEOUT_MS) {
        endForIdle();
        return;
      }
      if (now - last < ACTIVITY_WRITE_THROTTLE_MS) return;
      markActivity(now);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") touch();
    };

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, touch, { passive: true });
    }
    window.addEventListener("focus", touch);
    document.addEventListener("visibilitychange", onVisible);

    const interval = setInterval(() => {
      const last = readLastActivity();
      if (last !== null && Date.now() - last > IDLE_TIMEOUT_MS) endForIdle();
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, touch);
      }
      window.removeEventListener("focus", touch);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [token, clearSession, idleSignOutMessage]);

  // Keep every tab of the dashboard on the same session. The `storage` event
  // only fires in the *other* tabs, so this is how a login, a silent refresh or
  // a sign-out done in one tab reaches the rest. `key === null` means the whole
  // store was cleared.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key !== null && event.key !== TOKEN_KEY && event.key !== USER_KEY) return;
      const next = readStoredSession();
      setToken(next.token);
      setStored(next.token ? readStoredUser() : null);
      // The tab that purged for inactivity wrote the reason; show the same
      // notice here rather than dropping the operator on a bare login form.
      if (!next.token) setError(next.ended === "idle" ? idleSignOutMessage() : null);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [idleSignOutMessage]);

  // Silent refresh — schedule REFRESH_LEAD_SECONDS before the JWT's exp claim.
  // Emergency root tokens don't have an exp so we simply skip the timer.
  useEffect(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!token || stored?.emergency) return;
    const expMs = jwtExpiryMs(token);
    if (!expMs) return;
    const delay = expMs - Date.now() - REFRESH_LEAD_SECONDS * 1000;
    const timer = setTimeout(async () => {
      try {
        const res = await api.refresh(token);
        if (stored) persist(res.token, stored);
      } catch (err) {
        if (err instanceof ApiError && err.code === "E_UNAUTHORIZED") {
          clearSession();
        }
      }
    }, Math.max(delay, 5_000));
    refreshTimer.current = timer;
    return () => {
      clearTimeout(timer);
    };
  }, [token, stored, persist, clearSession]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user: stored?.user ?? null,
        emergency: stored?.emergency ?? false,
        loading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// Convenience for screens that only make sense once authenticated.
export function useToken(): string {
  const { token } = useAuth();
  if (!token) throw new Error("Missing token — component rendered before login");
  return token;
}
